import Modal from 'bootstrap/js/dist/modal';

export class Downloader {

    protected modal: Modal;
    protected form: HTMLFormElement;
    protected lastRequestedDownload?: string;

    constructor(
        protected buttons: NodeListOf<HTMLButtonElement>,
        protected modalElement: HTMLElement) {

        this.modal = new Modal(modalElement);
        this.modalElement.addEventListener('hidden.bs.modal', this.modalHidden.bind(this));

        this.bindButtons(buttons);

        this.form = this.modalElement.querySelector('form')!;
        this.form.addEventListener('submit', this.submit.bind(this));
        this.form.addEventListener('reset', this.reset.bind(this));
    }

    public bindButtons(buttons: NodeListOf<Element> | Element[]): void {
        buttons.forEach(btn => {
            btn.addEventListener('click', this.handleDownloadClick.bind(this));
        });
    }

    async handleDownloadClick(event: Event) {
        event.preventDefault();
        const button = event.currentTarget as HTMLAnchorElement;
        
        const expiration = new Date(parseInt(button.dataset.downloadTicketExpiration ?? '0') * 1000);
        if ( isNaN( expiration.getTime() ) || expiration < new Date() ) {
            this.lastRequestedDownload = button.href;
            this.modal.show();
            return;
        }

        await this.download(button.href);
    }

    async download(url: string) {
        
        try {
            const response = await fetch(url, {
                method: 'GET',
            });

            if ( [429, 403].includes( response.status ) && response.headers.get('X-Quota-Exceeded-Redirect') ) {
                window.location.href = response.headers.get('X-Quota-Exceeded-Redirect')!;
                return;
            }

            if ( !response.ok ) {
                if ( response.headers.get('X-Error-Message') === 'true' ) {
                    throw new Error('Failed to download file.', { cause: await response.text() });
                } else {
                    throw new Error('Failed to download file.', { cause: "Failed to download file." });
                }
            }

            const contentDisposition = response.headers.get('Content-Disposition');
            let fileName = 'downloaded-file';

            if (contentDisposition && contentDisposition.includes('attachment')) {
                console.log(contentDisposition);
                const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (filenameMatch != null && filenameMatch[1]) { 
                    fileName = filenameMatch[1].replace(/['"]/g, '');
                }
            }

            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = blobUrl;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(a);
        } catch (error: any) {
            if ( error.cause ) {
                window.alert(error.cause.trim());
            } else {
                window.alert('Download of this file failed.');
            }
            console.error('Failed to download file.', error);
        }

    }

    modalHidden() {
        this.form.reset();
    }

    reset() {
        const alert = this.form.querySelector('.alert')!;
        alert.classList.add('d-none');
        alert.setAttribute('aria-hidden', 'true');
    }

    async submit(event: Event) {
        event.preventDefault()
        event.stopPropagation()

        if (!this.form.checkValidity()) {
           
            this.form.classList.add('was-validated');
            return;

        }

        const button = this.modalElement.querySelector('button[type="submit"]')! as HTMLButtonElement;
        const formData = new FormData(this.form);
        try {
            button.disabled = true;
            const response = await fetch('download-ticket.php', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Failed to request ticket', { cause: response });
            }

            this.form.reset();
            this.modal.hide();

            const expiration = new Date( parseInt( await response.text() ) * 1000 );
            this.buttons.forEach(button => {
                button.dataset.downloadTicketExpiration = expiration.getTime().toString();
            });

            if (this.lastRequestedDownload) {
                await this.download(this.lastRequestedDownload);
                this.lastRequestedDownload = undefined;
            }

        } catch (error) {
            console.error('Error while requesting ticket', error);

            const alert = this.form.querySelector('.alert')!;
            alert.classList.remove('d-none');
            alert.removeAttribute('aria-hidden');
        } finally {            
            button.disabled = false;   
        };
    }
}